import React, { useState } from "react";
import { Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionShell, SCard, SRow, VerifiedBadge, fmtDateFr } from "./SettingsUI";
import { TwoFactorManager, SecurityAlertsCard } from "./TwoFactorManager";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";

const PwInput = ({ label, value, onChange, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <div className="relative">
        <Input type={show ? "text" : "password"} value={value} onChange={onChange} className="pr-10" data-testid={testid} autoComplete="new-password" />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={tfr("Afficher le mot de passe")}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const strengthChecks = (pw) => [
  { ok: pw.length >= 8, label: tfr("8 caractères min.") },  // tf applied at render
  { ok: /[a-z]/.test(pw) && /[A-Z]/.test(pw), label: "Majuscule + minuscule" },
  { ok: /\d/.test(pw), label: tfr("Un chiffre") },
];

export const SectionSecurity = ({ overview, reload }) => {
  const { tf } = useLang();
  const navigate = useNavigate();
  const s = overview?.security || {};
  const p = overview?.profile || {};
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const checks = strengthChecks(form.next);

  const changePassword = async () => {
    if (form.next !== form.confirm) { toast.error(tf("Les mots de passe ne correspondent pas")); return; }
    if (checks.some((c) => !c.ok)) { toast.error(tf("Le nouveau mot de passe est trop faible")); return; }
    setSaving(true);
    try {
      const r = await api.post("/account/change-password", { current_password: form.current, new_password: form.next });
      toast.success(`${tf("Mot de passe modifié.")} ${r.data.revoked_other_sessions} ${tf("autre(s) session(s) déconnectée(s).")}`);
      setForm({ current: "", next: "", confirm: "" });
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setSaving(false); }
  };

  return (
    <SectionShell title={tf("Sécurité")} desc={tf("Protégez votre compte et gérez vos méthodes d'authentification.")} testid="section-security">
      <SCard title={tf("Sécurité du compte")} testid="security-status-card" right={<ShieldCheck className="h-4 w-4 text-green-500" />}>
        <SRow label={tf("Mot de passe")} testid="security-password-row"
          value={s.has_password ? (s.password_changed_at ? `${tf("Dernière modification :")} ${fmtDateFr(s.password_changed_at)}` : tf("Défini")) : tf("Connexion Google (aucun mot de passe local)")} />
        <SRow label={tf("Authentification à deux facteurs")} testid="security-2fa-row"
          value={s.two_factor_enabled ? tf("Activée") : tf("Non activée")}
          badge={<VerifiedBadge verified={s.two_factor_enabled} label={s.two_factor_enabled ? tf("Activée") : tf("À configurer")} />} />
        <SRow label="Email" value={p.email} badge={<VerifiedBadge verified={p.email_verified} />} testid="security-email-row" />
        <SRow label={tf("Sessions actives")} testid="security-sessions-row"
          value={`${s.sessions_count} ${tf("appareil")}${s.sessions_count > 1 ? "s" : ""}`}
          badge={<Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => navigate("/settings/sessions")} data-testid="security-goto-sessions">{tf("Gérer")}</Button>} />
      </SCard>

      {s.has_password ? (
        <SCard title={tf("Changer le mot de passe")} desc={tf("Après modification, tous vos autres appareils seront déconnectés.")} testid="password-change-card">
          <div className="grid gap-4 max-w-md">
            <PwInput label={tf("Mot de passe actuel")} value={form.current} onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))} testid="pw-current-input" />
            <PwInput label={tf("Nouveau mot de passe")} value={form.next} onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))} testid="pw-new-input" />
            {form.next && (
              <div className="flex flex-wrap gap-2">
                {checks.map((c) => (
                  <span key={c.label} className={`text-[11px] rounded-full border px-2 py-0.5 ${c.ok ? "text-green-500 border-green-500/25" : "text-muted-foreground border-white/[0.1]"}`}>
                    {c.ok ? "✓ " : ""}{tf(c.label)}
                  </span>
                ))}
              </div>
            )}
            <PwInput label={tf("Confirmer le nouveau mot de passe")} value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} testid="pw-confirm-input" />
            <div className="flex justify-end">
              <Button onClick={changePassword} disabled={saving || !form.current || !form.next} data-testid="pw-save-btn">
                <KeyRound className="h-3.5 w-3.5 mr-1.5" />{saving ? tf("Modification…") : tf("Modifier le mot de passe")}
              </Button>
            </div>
          </div>
        </SCard>
      ) : (
        <SCard title={tf("Mot de passe")} testid="password-google-card">
          <p className="text-sm text-muted-foreground">{tf("Votre compte utilise la connexion Google. Aucun mot de passe Athlynx n'est défini — votre sécurité est gérée par Google.")}</p>
        </SCard>
      )}

      <TwoFactorManager overview={overview} reload={reload} />
      <SecurityAlertsCard />
    </SectionShell>
  );
};
